import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ApiFilterToggles,
  DayRange,
  InternalOpsPage,
  InternalOpsState,
  StatCard,
  UsageChart,
  formatCount,
  formatWhen,
  SortableTable,
  useApiFilters,
  useDayRange,
  useInternalOpsFetch,
  usageQuery,
  withQuery,
} from "./internalOpsUi";
import { API_ENDPOINTS } from "../../config/apiConfig";

const API_SERIES = [
  { key: "hits", name: "Hits", stroke: "#0f172a", fill: "#e2e8f0" },
  { key: "error_hits", name: "Errors", stroke: "#dc2626", fill: "#fecaca" },
];

function userHref(user, days, filters) {
  if (!user?.id) return null;
  return withQuery(`/internal/users/${user.id}`, usageQuery(days, filters));
}

function CallerLink({ user, days, filters }) {
  if (!user) return "—";
  const href = userHref(user, days, filters);
  if (!href) return user.username;
  return (
    <Link to={href} className="font-medium text-blue-700 hover:underline">
      {user.username}
    </Link>
  );
}

function EndpointTable({ app, endpoints, days, filters }) {
  return (
    <SortableTable
      rows={endpoints}
      empty="No calls in this window."
      rowKey={(row) => `${row.method}-${row.route}`}
      columns={[
        {
          key: "title",
          label: "Endpoint",
          sortValue: (row) => row.title,
          render: (row) => (
            <>
              <Link
                to={withQuery(`/internal/api-usage/${encodeURIComponent(app)}/endpoint`, usageQuery(days, filters, {
                  method: row.method,
                  route: row.route,
                }))}
                className="font-medium text-blue-700 hover:underline"
              >
                {row.title}
              </Link>
              <p className="mt-1 font-mono text-xs text-slate-500">
                {row.method} {row.route}
              </p>
            </>
          ),
        },
        { key: "hits", label: "Hits", sortValue: (row) => row.hits, render: (row) => formatCount(row.hits) },
        { key: "unique_users", label: "Users", sortValue: (row) => row.unique_users, render: (row) => formatCount(row.unique_users) },
        {
          key: "error_hits",
          label: "Errors",
          headerClassName: "text-red-700",
          cellClassName: "text-red-700",
          sortValue: (row) => row.error_hits,
          render: (row) => formatCount(row.error_hits),
        },
        {
          key: "avg_ms",
          label: "Avg",
          cellClassName: "text-slate-600",
          sortValue: (row) => row.avg_ms,
          render: (row) => (row.avg_ms == null ? "—" : `${row.avg_ms} ms`),
        },
        {
          key: "top_user",
          label: "Top caller",
          sortValue: (row) => row.top_user?.username || "",
          render: (row) => <CallerLink user={row.top_user} days={days} filters={filters} />,
        },
        {
          key: "last_seen",
          label: "Last seen",
          cellClassName: "text-slate-500",
          sortValue: (row) => row.last_seen,
          render: (row) => formatWhen(row.last_seen),
        },
      ]}
    />
  );
}

function CallerTable({ users, days, filters }) {
  return (
    <SortableTable
      rows={users}
      empty="No callers in this window."
      rowKey={(row) => row.id ?? "anonymous"}
      columns={[
        {
          key: "username",
          label: "User",
          sortValue: (row) => row.username || row.email || "",
          render: (row) => (
            <>
              <CallerLink user={row} days={days} filters={filters} />
              {row.email ? <p className="mt-1 text-xs text-slate-500">{row.email}</p> : null}
            </>
          ),
        },
        { key: "hits", label: "Hits", sortValue: (row) => row.hits, render: (row) => formatCount(row.hits) },
        {
          key: "last_seen",
          label: "Last seen",
          cellClassName: "text-slate-500",
          sortValue: (row) => row.last_seen,
          render: (row) => formatWhen(row.last_seen),
        },
      ]}
    />
  );
}

export default function InternalOpsApiUsage() {
  const [days, setDays] = useDayRange();
  const filters = useApiFilters();
  const { data, loading, error } = useInternalOpsFetch(
    withQuery(API_ENDPOINTS.internalOpsApiUsage, usageQuery(days, filters))
  );

  return (
    <InternalOpsPage
      title="API usage"
      subtitle="Calls grouped by the application that serves them, for the selected window."
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-4">
        <ApiFilterToggles
          excludeStaff={filters.excludeStaff}
          excludeAnonymous={filters.excludeAnonymous}
          onExcludeStaff={filters.setExcludeStaff}
          onExcludeAnonymous={filters.setExcludeAnonymous}
        />
        <DayRange days={days} onChange={setDays} />
      </div>
      <InternalOpsState loading={loading} error={error}>
        {data ? (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label={`Hits (${data.days}d)`} value={data.total} />
              <StatCard label="Unique users" value={data.unique_users} />
              <StatCard label="Authenticated" value={data.authenticated} />
              <StatCard label="Errors" value={data.error_hits} tone="danger" />
            </div>
            <UsageChart rows={data.by_day} series={API_SERIES} />
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">By application</h2>
              <SortableTable
                rows={data.apps}
                empty="No API calls recorded in this window."
                rowKey={(app) => app.app}
                columns={[
                  {
                    key: "title",
                    label: "Application",
                    sortValue: (app) => app.title,
                    render: (app) => (
                      <Link
                        to={withQuery(`/internal/api-usage/${encodeURIComponent(app.app)}`, usageQuery(days, filters))}
                        className="font-medium text-blue-700 hover:underline"
                      >
                        {app.title}
                      </Link>
                    ),
                  },
                  { key: "hits", label: "Hits", sortValue: (app) => app.hits, render: (app) => formatCount(app.hits) },
                  { key: "endpoint_count", label: "Endpoints", sortValue: (app) => app.endpoint_count, render: (app) => formatCount(app.endpoint_count) },
                  { key: "unique_users", label: "Users", sortValue: (app) => app.unique_users, render: (app) => formatCount(app.unique_users) },
                  {
                    key: "error_hits",
                    label: "Errors",
                    headerClassName: "text-red-700",
                    cellClassName: "text-red-700",
                    sortValue: (app) => app.error_hits,
                    render: (app) => formatCount(app.error_hits),
                  },
                  {
                    key: "last_seen",
                    label: "Last seen",
                    cellClassName: "text-slate-500",
                    sortValue: (app) => app.last_seen,
                    render: (app) => formatWhen(app.last_seen),
                  },
                ]}
              />
            </section>
            {data.note ? <p className="text-xs text-slate-500">{data.note}</p> : null}
          </div>
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}

export function InternalOpsApiUsageApp() {
  const { appLabel } = useParams();
  const [days, setDays] = useDayRange();
  const filters = useApiFilters();
  const { data, loading, error } = useInternalOpsFetch(
    withQuery(API_ENDPOINTS.internalOpsApiUsageApp(appLabel), usageQuery(days, filters))
  );

  return (
    <InternalOpsPage
      title={data?.title || "Application"}
      subtitle={data ? `${data.app} · endpoints and the users calling them` : "Endpoints in this application."}
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <Link to={withQuery("/internal/api-usage", usageQuery(days, filters))} className="text-sm font-medium text-blue-700 hover:underline">
            All applications
          </Link>
          {appLabel === "charging_station_module" ? (
            <Link
              to={withQuery(`/internal/api-usage/${encodeURIComponent(appLabel)}/parcels`, usageQuery(days, filters))}
              className="text-sm font-medium text-blue-700 hover:underline"
            >
              Parcel popularity
            </Link>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <ApiFilterToggles
            excludeStaff={filters.excludeStaff}
            excludeAnonymous={filters.excludeAnonymous}
            onExcludeStaff={filters.setExcludeStaff}
            onExcludeAnonymous={filters.setExcludeAnonymous}
          />
          <DayRange days={days} onChange={setDays} />
        </div>
      </div>
      <InternalOpsState loading={loading} error={error}>
        {data ? (
          <div className="space-y-8">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label={`Hits (${data.days}d)`} value={data.total} />
              <StatCard label="Unique users" value={data.unique_users} />
              <StatCard label="Authenticated" value={data.authenticated} />
              <StatCard label="Errors" value={data.error_hits} tone="danger" />
            </div>
            <UsageChart rows={data.by_day} series={API_SERIES} />
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Endpoints</h2>
              <EndpointTable app={data.app} endpoints={data.endpoints} days={days} filters={filters} />
            </section>
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Users</h2>
              <CallerTable users={data.users} days={days} filters={filters} />
            </section>
          </div>
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}

export function InternalOpsApiUsageEndpoint() {
  const { appLabel } = useParams();
  const [params] = useSearchParams();
  const method = params.get("method") || "";
  const route = params.get("route") || "";
  const [days, setDays] = useDayRange();
  const filters = useApiFilters();
  const { data, loading, error } = useInternalOpsFetch(
    withQuery(API_ENDPOINTS.internalOpsApiUsageEndpoint(appLabel), usageQuery(days, filters, { method, route }))
  );
  const endpoint = data?.endpoint;

  return (
    <InternalOpsPage
      title={endpoint?.title || "Endpoint"}
      subtitle={endpoint ? `${endpoint.method} ${endpoint.route}` : "Callers for this endpoint."}
    >
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          <Link to={withQuery("/internal/api-usage", usageQuery(days, filters))} className="font-medium text-blue-700 hover:underline">
            All applications
          </Link>
          <span className="px-2 text-slate-300">/</span>
          <Link
            to={withQuery(`/internal/api-usage/${encodeURIComponent(appLabel)}`, usageQuery(days, filters))}
            className="font-medium text-blue-700 hover:underline"
          >
            {data?.title || appLabel}
          </Link>
        </p>
        <div className="flex flex-wrap items-center gap-4">
          <ApiFilterToggles
            excludeStaff={filters.excludeStaff}
            excludeAnonymous={filters.excludeAnonymous}
            onExcludeStaff={filters.setExcludeStaff}
            onExcludeAnonymous={filters.setExcludeAnonymous}
          />
          <DayRange days={days} onChange={setDays} />
        </div>
      </div>
      <InternalOpsState loading={loading} error={error}>
        {data && endpoint ? (
          <div className="space-y-8">
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard label={`Hits (${data.days}d)`} value={endpoint.hits} />
              <StatCard label="Unique users" value={endpoint.unique_users} />
              <StatCard label="Errors" value={endpoint.error_hits} tone="danger" />
            </div>
            <UsageChart rows={data.by_day} series={API_SERIES} />
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Users calling this API</h2>
              <CallerTable users={data.users} days={days} filters={filters} />
            </section>
          </div>
        ) : null}
      </InternalOpsState>
    </InternalOpsPage>
  );
}
